import { FileText, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import * as React from 'react';
import toast from 'react-hot-toast';
import {
  PromptFormDialog,
  type EditPromptData,
} from '@/components/case-partner/PromptFormDialog';
import type { PromptResponse } from '@/components/case-partner/types';
import Loading from '@/components/shared/Loading';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/NewButton';
import { SimpleTableActionsDropdown } from '@/components/ui/simple-table-actions-dropdown';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  createAdminPromptLibraryApi,
  useAdminFirmPrompts,
} from '@/hooks/useAdminFirmPrompts';
import { apiErrorMessage } from '@/lib/api-client';
import { FirmAdminEmptyState, FirmAdminShell } from './ShellTabs';

const adminPromptApi = createAdminPromptLibraryApi();

function toEditPromptData(prompt: PromptResponse): EditPromptData {
  return {
    promptGroupId: prompt.prompt_group_id,
    title: prompt.title,
    content: prompt.content,
    categoryName: prompt.category_name,
    scope: prompt.scope,
    canManage: prompt.can_manage,
  };
}

export function PromptsTab() {
  const searchInputId = React.useId();
  const [query, setQuery] = React.useState('');
  const [formOpen, setFormOpen] = React.useState(false);
  const [editPrompt, setEditPrompt] = React.useState<EditPromptData | undefined>(undefined);
  const [promptToDelete, setPromptToDelete] = React.useState<PromptResponse | null>(null);
  const [deletingPromptGroupId, setDeletingPromptGroupId] = React.useState<string | null>(null);

  const { prompts, isLoading, isError, mutate } = useAdminFirmPrompts({ enabled: true });

  const visiblePrompts = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return prompts;
    return prompts.filter(
      (prompt) =>
        prompt.title.toLowerCase().includes(q)
        || prompt.category_name.toLowerCase().includes(q)
        || (prompt.created_by_name ?? '').toLowerCase().includes(q),
    );
  }, [prompts, query]);

  const handleNewPrompt = React.useCallback(() => {
    setEditPrompt(undefined);
    setFormOpen(true);
  }, []);

  const handleEdit = React.useCallback((prompt: PromptResponse) => {
    setEditPrompt(toEditPromptData(prompt));
    setFormOpen(true);
  }, []);

  const handlePromptSaved = React.useCallback(() => {
    void mutate();
  }, [mutate]);

  const handleDelete = React.useCallback(async () => {
    if (!promptToDelete) return;
    const prompt = promptToDelete;
    setDeletingPromptGroupId(prompt.prompt_group_id);
    try {
      await adminPromptApi.deletePrompt(prompt.prompt_group_id);
      await mutate(
        (current) => (current ?? []).filter((item) => item.prompt_group_id !== prompt.prompt_group_id),
        { revalidate: false },
      );
      void mutate();
      setPromptToDelete(null);
      toast.success('Prompt deleted');
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to delete prompt');
    } finally {
      setDeletingPromptGroupId(null);
    }
  }, [mutate, promptToDelete]);

  return (
    <FirmAdminShell
      title="Firm prompts"
      description="Shared prompts visible to everyone in your firm."
      action={(
        <Button
          icon={Plus}
          text="New prompt"
          variant="outline"
          onClick={handleNewPrompt}
        />
      )}
    >
      <div className="space-y-4">
        <div className="relative w-full max-w-md">
          <label htmlFor={searchInputId} className="sr-only">Search prompts</label>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            id={searchInputId}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search prompts"
            className="h-10 pl-9"
          />
        </div>

        {isLoading ? (
          <div className="flex h-56 items-center justify-center">
            <Loading />
          </div>
        ) : isError ? (
          <FirmAdminEmptyState
            icon={FileText}
            title="Could not load prompts"
            description="Try refreshing the page. If this keeps happening, contact support."
          />
        ) : (
          <div className="overflow-hidden rounded-md border bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Created by</TableHead>
                  <TableHead className="w-16">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visiblePrompts.length ? (
                  visiblePrompts.map((prompt) => (
                    <TableRow key={prompt.prompt_group_id}>
                      <TableCell className="min-w-0">
                        <div className="min-w-0" title={prompt.title}>
                          <p className="truncate font-medium text-slate-900">{prompt.title}</p>
                          <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{prompt.content}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-700">
                        {prompt.category_name}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {prompt.created_by_name || '—'}
                      </TableCell>
                      <TableCell>
                        <SimpleTableActionsDropdown
                          label={`Actions for ${prompt.title}`}
                          actions={[
                            {
                              label: 'Edit',
                              icon: Pencil,
                              onSelect: () => handleEdit(prompt),
                            },
                            {
                              label: 'Delete',
                              icon: Trash2,
                              destructive: true,
                              disabled: !prompt.can_manage || Boolean(deletingPromptGroupId),
                              loading: deletingPromptGroupId === prompt.prompt_group_id,
                              onSelect: () => setPromptToDelete(prompt),
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="p-0">
                      <FirmAdminEmptyState
                        icon={FileText}
                        title={query.trim() ? 'No matching firm prompts' : 'No firm prompts yet'}
                        description={
                          query.trim()
                            ? 'Try a different search term.'
                            : 'Create a firm prompt to share it with everyone in your organisation.'
                        }
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <PromptFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditPrompt(undefined);
        }}
        onPromptSaved={handlePromptSaved}
        editPrompt={editPrompt}
        api={adminPromptApi}
        defaultScope="tenant"
        allowedScopes={['tenant']}
      />

      <AlertDialog
        open={!!promptToDelete}
        onOpenChange={(open) => {
          if (!open) setPromptToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete firm prompt?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{promptToDelete?.title}&quot; and its version history
              for everyone in the firm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingPromptGroupId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void handleDelete();
              }}
              disabled={!promptToDelete || deletingPromptGroupId === promptToDelete.prompt_group_id}
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
            >
              {deletingPromptGroupId === promptToDelete?.prompt_group_id ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FirmAdminShell>
  );
}
